import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ScatterplotLayer } from '@deck.gl/layers';
import { getInfraId } from '@/utils/infraStatus';
import type { Infrastructure } from '../mapModes/MapModeStrategy';
import type { ViewState } from './useMapViewState';

const PULSE_PERIOD_MS = 1200;
const PULSE_COUNT = 3;
const PULSE_DURATION_MS = PULSE_PERIOD_MS * PULSE_COUNT;

interface PulseTarget {
	id: number | string;
	key: number;
}

interface PulseDatum {
	position: [number, number];
}

interface UsePulseHighlightParams {
	infras: Infrastructure[] | null | undefined;
	zoom: number;
	setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
}

export function usePulseHighlight({ infras, zoom, setViewState }: UsePulseHighlightParams) {
	const [pulse, setPulse] = useState<PulseTarget | null>(null);
	const [pulsePhase, setPulsePhase] = useState<number>(0);
	const infrasRef = useRef<Infrastructure[] | null | undefined>(infras);
	infrasRef.current = infras;

	useEffect(() => {
		if (!pulse) return;
		const start = performance.now();
		let frameId: number;
		const loop = (now: number) => {
			const elapsed = now - start;
			if (elapsed >= PULSE_DURATION_MS) {
				setPulse(null);
				setPulsePhase(0);
				return;
			}
			setPulsePhase(elapsed);
			frameId = requestAnimationFrame(loop);
		};
		frameId = requestAnimationFrame(loop);
		return () => cancelAnimationFrame(frameId);
	}, [pulse?.key]);

	const pulseAsset = useMemo<PulseDatum | null>(() => {
		if (!pulse) return null;
		const target = (infras ?? []).find(
			(infra) => String(getInfraId(infra)) === String(pulse.id),
		);
		if (!target) return null;
		const lng = Number.parseFloat(target.longitude);
		const lat = Number.parseFloat(target.latitude);
		if (Number.isNaN(lng) || Number.isNaN(lat)) return null;
		return { position: [lng, lat] };
	}, [pulse, infras]);

	const pulseLayers = useMemo(() => {
		if (!pulseAsset) return [];
		const t = (pulsePhase % PULSE_PERIOD_MS) / PULSE_PERIOD_MS;
		const fade = (1 - t) * (1 - t);
		const baseRadius = Math.max(15, 30 * (zoom / 10));
		const radius = baseRadius * (1 + t * 2.2);
		const ringAlpha = Math.round(220 * fade);
		const fillAlpha = Math.round(70 * fade);
		const fillColor: [number, number, number, number] = [4, 98, 140, fillAlpha];
		const lineColor: [number, number, number, number] = [4, 98, 140, ringAlpha];
		return [
			new ScatterplotLayer<PulseDatum>({
				id: 'pulse-asset',
				data: [pulseAsset],
				pickable: false,
				getPosition: (d) => d.position,
				stroked: true,
				filled: true,
				radiusUnits: 'pixels',
				getRadius: radius,
				getFillColor: fillColor,
				getLineColor: lineColor,
				lineWidthUnits: 'pixels',
				getLineWidth: 4,
				updateTriggers: {
					getRadius: [pulsePhase, zoom],
					getFillColor: [pulsePhase],
					getLineColor: [pulsePhase],
				},
			}),
		];
	}, [pulseAsset, pulsePhase, zoom]);

	const zoomToAsset = useCallback((infrastructureId?: number | string) => {
		if (infrastructureId == null) return;
		const target = (infrasRef.current ?? []).find(
			(infra) => String(getInfraId(infra)) === String(infrastructureId),
		);
		if (!target) return;
		const lng = Number.parseFloat(target.longitude);
		const lat = Number.parseFloat(target.latitude);
		if (Number.isNaN(lng) || Number.isNaN(lat)) return;
		setViewState((prev) => ({
			...prev,
			longitude: lng,
			latitude: lat,
			zoom: Math.max(prev.zoom, 17),
		}));
		setPulse({ id: infrastructureId, key: Date.now() });
	}, []);

	return { pulseLayers, zoomToAsset };
}
